import { addCadFile } from '@api/projects';
import DragAndDrop from '@components/Common/DragAndDrop.js';
import IconButton from '@components/Common/IconButton.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import { IS_MOBILE } from '@constants/global';
import {
  BanIcon,
  CalendarIcon,
  FileIcon,
  FolderIcon,
  MailIcon,
  PackageIcon,
  PlusRoundedIcon,
  ShoppingCartIcon,
  UsersIcon,
} from '@icons';
import ProjectsState, {
  CurrentFolderId,
  CurrentItemsState,
} from '@states/Projects';
import UserState from '@states/User';
import { UICheckbox, UIDiv, UITable, UIText } from '@ui';
import { updateColumnValue } from '@utils/columnUtils';
import {
  convertAllItemsToCSV,
  createCSVFileDownload,
  createMissingColumns,
  downloadTemplateForType,
  onDownloadTemplate,
  onUploadCsv,
} from '@utils/fileManagement';
import { addProject } from '@utils/projectUtils';

// Template icon mapping to icon components
const TEMPLATE_ICON_COMPONENTS = {
  folder: FolderIcon,
  users: UsersIcon,
  'shopping-cart': ShoppingCartIcon,
  calendar: CalendarIcon,
  mail: MailIcon,
  file: FileIcon,
  package: PackageIcon,
};

export default class ProjectDataActions extends UIDiv {
  constructor() {
    super();
    this.currFile = null;
    this.currentCsvFile = null;

    this.#render();
  }

  #render() {
    this.projectDataActions = new IconButton({
      iconName: 'data',
      color: 'none',
      text: IS_MOBILE()
        ? ''
        : i18next.t('pages.projects.projectDataActions.title'),
      iconSize: IS_MOBILE() ? 21 : 16,
      onClick: () => {}, // Will be set after popover creation
      buttonClass: 'min-h-8 border border-gray-300 bg-white hover:bg-gray-50',
    });

    // Create popover content
    const popoverContent = this.createPopoverContent();

    // Create popover
    this.projectDataActions.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: this.projectDataActions,
        content: popoverContent,
        placement: 'bottom-start',
        hideOnSelect: true,
        hideOnScroll: false,
        className: 'max-h-96 overflow-hidden flex flex-col',
      });
    });

    this.add(this.projectDataActions);
  }

  createPopoverContent() {
    const container = new UIDiv().setClass('flex flex-col h-full mb-1');

    // upload CSV button
    const uploadCsvBtn = this.#createImportCsvButton();
    container.add(uploadCsvBtn);
    // download csv button
    const downloadCsvBtn = this.#createExportCsvButton();
    container.add(downloadCsvBtn);

    container.add(new UIDiv().setClass('h-[2px] my-2 w-full bg-gray-100')); // Divider

    // download template button
    const downloadTemplateBtn = this.#createDownloadTemplateButton();
    container.add(downloadTemplateBtn);

    return container;
  }

  #createImportCsvButton() {
    const uploadCsvBtn = new IconButton({
      iconName: 'upload',
      text: i18next.t('pages.projects.projectDataActions.importCsv'),
      iconSize: 20,
      buttonClass: 'hover:bg-gray-50 rounded-none !filter-none',
      onClick: () => this.#showCsvUploadModal(),
    });

    return uploadCsvBtn;
  }

  #createExportCsvButton() {
    const downloadAllBtn = new IconButton({
      iconName: 'download', // Export icon is typically a download icon
      text: i18next.t('pages.projects.projectDataActions.exportCsv'),
      iconSize: 20,
      buttonClass: 'hover:bg-gray-50 rounded-none !filter-none',
      onClick: async () => {
        try {
          // Get all current items from the project view container
          const allItems = CurrentItemsState.currentItems || [];

          if (allItems.length === 0) {
            toast.error(
              i18next.t('pages.projects.projectDataActions.noItemsForExport'),
            );
            return;
          }

          // Convert all items to CSV and download directly
          const { file } = await convertAllItemsToCSV(
            allItems,
            // if reconstructions are present then it is not the root folder and we can use the parentId
            CurrentFolderId.currentFolderId,
          );

          // Create a download link for the CSV file
          createCSVFileDownload(file);

          toast.success(
            i18next.t('pages.projects.projectDataActions.exportedCsvFile', {
              count: allItems.length,
            }),
          );
        } catch (error) {
          console.error('Error exporting all items:', error);
          toast.error(
            i18next.t('pages.projects.projectDataActions.exportFailed'),
          );
        }
      },
    });
    return downloadAllBtn;
  }

  #createDownloadTemplateButton() {
    const downloadTemplateBtn = new IconButton({
      iconName: 'download',
      text: i18next.t('pages.projects.projectDataActions.downloadTemplate'),
      iconSize: 20,
      buttonClass:
        'hover:bg-gray-50 rounded-none !filter-none whitespace-nowrap',
      onClick: () => {
        Popover.hide();
        this.#showTemplateSelectionModal();
      },
    });
    return downloadTemplateBtn;
  }

  #showTemplateSelectionModal() {
    const content = new UIDiv().setClass('flex flex-col gap-2');

    // Add "Current Project" option first (original behavior)
    const currentProjectBtn = new IconButton({
      iconName: 'folder',
      text: i18next.t(
        'pages.projects.projectDataActions.currentProjectTemplate',
      ),
      iconSize: 20,
      buttonClass:
        'hover:bg-gray-100 rounded-lg p-3 !filter-none whitespace-nowrap w-full text-left border border-gray-200',
      onClick: () => {
        onDownloadTemplate(CurrentFolderId.currentFolderId);
        this.templateModal.close();
      },
    });
    content.add(currentProjectBtn);

    // // Add divider with label
    // const dividerContainer = new UIDiv().setClass(
    //   'flex items-center gap-2 my-2',
    // );
    // dividerContainer.add(new UIDiv().setClass('flex-1 h-[1px] bg-gray-200'));
    // const dividerLabel = new UIText(
    //   i18next.t('pages.projects.projectDataActions.orSelectTemplate'),
    // );
    // dividerLabel.setClass('text-xs text-gray-500');
    // dividerContainer.add(dividerLabel);
    // dividerContainer.add(new UIDiv().setClass('flex-1 h-[1px] bg-gray-200'));
    // content.add(dividerContainer);

    // // Add template type options in a grid
    // const templateGrid = new UIDiv().setClass('grid grid-cols-2 gap-2');

    // const templateTypes = getTemplateTypes();
    // templateTypes.forEach(template => {
    //   const templateBtn = this.#createTemplateCard(template);
    //   templateGrid.add(templateBtn);
    // });

    // content.add(templateGrid);

    this.templateModal = new Modal(
      i18next.t('pages.projects.projectDataActions.selectTemplateTitle'),
      {
        showCancel: true,
        showConfirm: false,
        cancelText: i18next.t('common.close'),
        modalContentClass: '!max-w-md',
        contentContainerClass: 'p-4',
        content: content,
      },
    );

    this.templateModal.show();
  }

  #createTemplateCard(template) {
    const card = new UIDiv();
    card.setClass(
      'flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer transition-all hover:border-[var(--o-hive-primary-60)] hover:bg-gray-50',
    );

    // Icon container with icon component
    const iconContainer = new UIDiv();
    iconContainer.setClass(
      'flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600',
    );
    const IconComponent =
      TEMPLATE_ICON_COMPONENTS[template.icon] ||
      TEMPLATE_ICON_COMPONENTS.folder;
    const icon = new IconComponent({ size: 18 });
    iconContainer.add(icon);
    card.add(iconContainer);

    // Text
    const name = new UIText(
      i18next.t(`pages.projects.templates.${template.id}.name`, {
        defaultValue: template.name,
      }),
    );
    name.setClass('text-sm font-medium text-gray-900');
    card.add(name);

    card.onClick(() => {
      downloadTemplateForType(template.id);
      this.templateModal.close();
    });

    return card;
  }

  #showCsvUploadModal() {
    const inputField = new DragAndDrop({
      supportedTypesText: i18next.t(
        'pages.projects.projectDataActions.supportedFileTypes',
      ),
      acceptedFileTypes: '.csv',
      callBack: selectedFiles => (this.currFile = selectedFiles),
      multiple: false,
    });
    const modal = new Modal(
      i18next.t('pages.projects.projectDataActions.csvUploadRequirements'),
      {
        description: i18next.t(
          'pages.projects.projectDataActions.csvUploadDescription',
        ),
        confirmText: i18next.t('pages.projects.projectDataActions.continue'),
        cancelText: i18next.t('common.cancel'),
        content: inputField,
        handleOnAction: async () => {
          const file = Array.from(this.currFile.values()).map(fileData => ({
            file: fileData.file,
          }));
          if (!file || file.length === 0)
            return toast.error(
              i18next.t('pages.projects.projectDataActions.noFileSelected'),
            );

          try {
            // Parse and validate the CSV file
            const currentFolderId = CurrentFolderId.currentFolderId;
            this.currentCsvFile = file[0].file; // Store the file for re-parsing
            const result = await onUploadCsv(file[0].file, currentFolderId);

            // Check if there are missing columns
            if (result.missingColumns && result.missingColumns.length > 0) {
              this.#showMissingColumnsModal(result);
            } else {
              // Show confirmation modal with parsed data
              this.#showCsvConfirmationModal(result);
            }
            return true; // Close the initial modal
          } catch (error) {
            console.error('Error parsing CSV:', error);
            toast.error(
              error.message ||
                i18next.t('pages.projects.projectDataActions.failedToParseCSV'),
            );
            return false; // Keep the modal open
          }
        },
      },
    );

    modal.show();
  }

  #showMissingColumnsModal(parsedData) {
    const { missingColumns } = parsedData;

    // Create content container
    const contentContainer = new UIDiv().setClass('flex flex-col gap-4');

    // Info section
    const infoContainer = new UIDiv().setClass(
      'flex flex-col gap-2 p-4 bg-blue-50 rounded-lg',
    );
    const infoTitle = new UIText(
      i18next.t('pages.projects.projectDataActions.missingColumnsDetected'),
    ).setClass('font-semibold text-blue-800');
    const infoText = new UIText(
      i18next.t('pages.projects.projectDataActions.missingColumnsDescription'),
    ).setClass('text-blue-700');
    infoContainer.add(infoTitle, infoText);

    // Columns list with toggles
    const columnsContainer = new UIDiv().setClass('flex flex-col gap-2');
    const columnsToCreate = new Map();

    // "Select All" toggle
    const selectAllContainer = new UIDiv().setClass(
      'flex items-center justify-between p-2 bg-gray-50 rounded border',
    );
    const selectAllLabel = new UIText(
      i18next.t('pages.projects.projectDataActions.selectAll'),
    ).setClass('font-medium');
    const selectAllCheckbox = new UICheckbox('', true, false);
    let allSelected = true;

    const updateSelectAllState = () => {
      const allColumnsSelected = missingColumns.every(col =>
        columnsToCreate.get(col.title),
      );
      selectAllCheckbox.setChecked(allColumnsSelected);

      allSelected = allColumnsSelected;
    };

    selectAllContainer.add(selectAllLabel, selectAllCheckbox);
    selectAllContainer.onClick(() => {
      allSelected = !allSelected;
      missingColumns.forEach(col => {
        columnsToCreate.set(col.title, allSelected);
      });
      updateSelectAllState();
      renderColumnsList();
    });

    const renderColumnsList = () => {
      columnsContainer.clear();

      missingColumns.forEach(column => {
        const columnContainer = new UIDiv().setClass(
          'flex items-center justify-between p-2 border rounded hover:bg-gray-50',
        );

        const columnInfo = new UIDiv().setClass('flex flex-col');
        const columnTitle = new UIText(column.title).setClass('font-medium');
        const columnType = new UIText(
          i18next.t('pages.projects.projectDataActions.type', {
            type: column.type,
          }),
        ).setClass('text-sm text-gray-500');
        columnInfo.add(columnTitle, columnType);

        const isSelected = columnsToCreate.get(column.title);
        const checkbox = new UICheckbox('', isSelected, false);

        columnContainer.onClick(() => {
          const currentState = columnsToCreate.get(column.title);
          columnsToCreate.set(column.title, !currentState);
          updateSelectAllState();
          renderColumnsList();
        });

        columnContainer.add(columnInfo, checkbox);
        columnsContainer.add(columnContainer);
      });
    };

    // Initialize all columns as selected
    missingColumns.forEach(col => {
      columnsToCreate.set(col.title, true);
    });

    renderColumnsList();

    contentContainer.add(infoContainer, selectAllContainer, columnsContainer);

    const missingColumnsModal = new Modal(
      i18next.t('pages.projects.projectDataActions.selectColumnsToCreate'),
      {
        content: contentContainer,
        confirmText: i18next.t(
          'pages.projects.projectDataActions.continueToPreview',
        ),
        cancelText: i18next.t(
          'pages.projects.projectDataActions.skipAllMissingColumns',
        ),
        handleOnAction: async () => {
          try {
            const selectedColumns = missingColumns.filter(col =>
              columnsToCreate.get(col.title),
            );

            // Don't create columns here, just pass the selection to confirmation modal
            const updatedParsedData = {
              ...parsedData,
              selectedMissingColumns: selectedColumns,
            };

            this.#showCsvConfirmationModal(updatedParsedData);
            return true;
          } catch (error) {
            console.error('Error processing column selection:', error);
            toast.error(
              i18next.t(
                'pages.projects.projectDataActions.failedToProcessColumnSelection',
              ),
            );
            return false;
          }
        },
        handleOnCancel: () => {
          // Skip missing columns - proceed with available columns only
          const updatedParsedData = {
            ...parsedData,
            selectedMissingColumns: [],
          };
          this.#showCsvConfirmationModal(updatedParsedData);
          return true;
        },
      },
    );

    missingColumnsModal.show();
  }

  #showCsvConfirmationModal(parsedData) {
    const {
      extractedData,
      newColumnValues,
      headers,
      types,
      missingColumns,
      selectedMissingColumns = [],
    } = parsedData;
    const currentFolderId = CurrentFolderId.currentFolderId;

    // Validate data before showing modal
    if (!extractedData || extractedData.length === 0) {
      toast.error(
        i18next.t('pages.projects.projectDataActions.noValidDataFound'),
      );
      return;
    }

    // Create content container
    const contentContainer = new UIDiv().setClass(
      'flex flex-col gap-4 max-h-[70vh] overflow-hidden',
    );

    // Show columns that will be created
    if (selectedMissingColumns && selectedMissingColumns.length > 0) {
      const createColumnsList = selectedMissingColumns
        .map(col => `${col.title} (${col.type})`)
        .join(', ');

      const createColumnsContainer = new UIDiv().setClass(
        'flex items-center gap-2',
      );
      const plusIcon = new PlusRoundedIcon({
        size: 16,
        strokeColor: '#16a34a',
        color: 'none',
      });
      const createColumnsText = new UIText(
        `${i18next.t('pages.projects.projectDataActions.columnsToBeCreated')}: ${createColumnsList}`,
      ).setClass('text-sm font-medium');

      createColumnsContainer.add(plusIcon, createColumnsText);
      contentContainer.add(createColumnsContainer);
    }

    // Warning section if there are still missing columns (not selected for creation)
    const skippedColumns =
      missingColumns?.filter(
        col =>
          !selectedMissingColumns.some(
            selected => selected.title === col.title,
          ),
      ) || [];

    if (skippedColumns.length > 0) {
      const missingColumnsList = skippedColumns
        .map(col => `${col.title} (${col.type})`)
        .join(', ');

      const skippedColumnsContainer = new UIDiv().setClass(
        'flex items-center gap-2',
      );
      const banIcon = new BanIcon({
        size: 16,
        strokeColor: '#ef4444',
        color: 'none',
      });
      const missingWarningText = new UIText(
        `${i18next.t('pages.projects.projectDataActions.skippedColumns')}: ${missingColumnsList}`,
      ).setClass('text-sm font-medium');

      skippedColumnsContainer.add(banIcon, missingWarningText);
      contentContainer.add(skippedColumnsContainer);
    }

    // Table container with scroll
    const tableContainer = new UIDiv().setClass(
      'flex-1 overflow-auto border border-gray-200 rounded-lg bg-white',
    );
    const previewTable = this.#createPreviewTable(
      headers,
      types,
      extractedData,
      newColumnValues,
      parsedData.headersNameTypes, // Pass the header info to filter missing columns
      selectedMissingColumns, // Pass selected missing columns to show them in preview
      parsedData.csvData, // Pass CSV data to extract values for missing columns
    );
    tableContainer.add(previewTable);

    contentContainer.add(tableContainer);

    const confirmText = `${extractedData.length} ${'tasks' /* Using fallback since tasks key is missing from locales */}`;
    const hasColumnsToCreate =
      selectedMissingColumns && selectedMissingColumns.length > 0;
    const buttonText = hasColumnsToCreate
      ? i18next.t('pages.projects.projectDataActions.createColumnsAndUpload', {
          count: confirmText,
        })
      : i18next.t('pages.projects.projectDataActions.upload', {
          count: confirmText,
        });

    const modalDescription = hasColumnsToCreate
      ? i18next.t(
          'pages.projects.projectDataActions.createColumnsDescription',
          {
            count: selectedMissingColumns.length,
            plural: selectedMissingColumns.length !== 1 ? 's' : '',
            uploadCount: confirmText,
          },
        )
      : i18next.t('pages.projects.projectDataActions.uploadDescription', {
          count: confirmText,
        });

    const confirmationModal = new Modal(
      i18next.t('pages.projects.projectDataActions.confirmCsvUpload'),
      {
        content: contentContainer,
        description: modalDescription,
        confirmText: buttonText,
        cancelText: i18next.t('common.cancel'),
        modalContentClass: '!max-w-6xl max-h-[80vh]',
        contentContainerClass: 'p-0',
        handleOnAction: async () => {
          try {
            // Call the actual upload method
            await this.#uploadProjectsAndColumnData(parsedData);
            toast.success(
              i18next.t(
                'pages.projects.projectDataActions.csvDataUploadedSuccessfully',
              ),
            );
            return true;
          } catch (error) {
            console.error('Error uploading CSV data:', error);
            toast.error(
              i18next.t(
                'pages.projects.projectDataActions.failedToUploadCsvData',
              ),
            );
            return false;
          }
        },
      },
    );

    confirmationModal.show();
  }

  async #uploadProjectsAndColumnData(parsedData) {
    try {
      const {
        extractedData,
        newColumnValues,
        selectedMissingColumns = [],
      } = parsedData;

      const currentFolderId = CurrentFolderId.currentFolderId;

      // Step 1: Create missing columns if any were selected
      if (selectedMissingColumns.length > 0) {
        await createMissingColumns(
          selectedMissingColumns,
          currentFolderId,
          parsedData.csvData,
        );

        // Re-parse the CSV data with the newly created columns
        const updatedResult = await onUploadCsv(
          this.currentCsvFile,
          currentFolderId,
        );

        // Update the parsed data with the new column information
        parsedData.extractedData = updatedResult.extractedData;
        parsedData.newColumnValues = updatedResult.newColumnValues;
        parsedData.headersNameTypes = updatedResult.headersNameTypes;
      }

      // Step 2: Create projects or tasks with column data
      // Create projects if currentFolderId is null
      if (currentFolderId === null) {
        const createdProjects = await Promise.all(
          parsedData.extractedData.map(projectName => addProject(projectName)),
        );

        // Update column values for each created project
        for (let i = 0; i < createdProjects.length; i++) {
          const project = createdProjects[i];
          const columnData = parsedData.newColumnValues[i];

          for (const [columnTitle, columnInfo] of Object.entries(columnData)) {
            //   columnId, entityType, entityId, value
            await updateColumnValue(
              columnInfo.id,
              'folder',
              project.id,
              columnInfo.value,
            );
          }
        }
      } else {
        // columnValues needs to be [{columnId: number, value: string}]
        const cadFiles = await Promise.all(
          parsedData.extractedData.map(projectName => {
            // Use the current index to get the correct row from newColumnValues
            const idx = parsedData.extractedData.indexOf(projectName);
            const columnValues = Object.entries(
              parsedData.newColumnValues[idx],
            ).map(([header, columnInfo]) => ({
              columnId: columnInfo.id,
              value: columnInfo.value,
            }));

            return addCadFile({
              name: projectName.trim(),
              modelFolderId: currentFolderId,
              reconstructions: [],
              columnValues: columnValues,
            }).then(response => ({
              response,
              columnValues, // keep the sent columnValues
            }));
          }),
        );

        // update projectState
        const newTask = cadFiles.map(({ response, columnValues }) => ({
          id: response.response.cadFileID,
          name: response.response.name || 'Untitled', // Using fallback since untitled key is missing from locales
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          thumbnail: null,
          parentId: currentFolderId,
          sharingId: response.response.sharingId,
          uid: UserState.user.uid,
          organizationId: ProjectsState.projects.rootFolderOrganizationId,
          reconstructions: [],
          columnValues: columnValues || [],
        }));

        const updatedTasks = [...newTask, ...ProjectsState.projects.tasks];
        ProjectsState.setProjects({
          ...ProjectsState.projects,
          tasks: updatedTasks,
        });
      }
    } catch (error) {
      console.error('Error uploading projects and column data:', error);
      toast.error(
        i18next.t(
          'pages.projects.projectDataActions.failedToUploadProjectsAndColumnData',
        ),
      );
    }
  }

  #createPreviewTable(
    headers,
    types,
    projectNames,
    columnValues,
    headersNameTypes = null,
    selectedMissingColumns = [],
    csvData = null,
  ) {
    const table = new UITable();
    table.setClass('w-full divide-y divide-gray-200 whitespace-nowrap');

    // Build the columns to show: existing columns + selected missing columns
    let displayHeaders = [];
    let displayTypes = [];

    if (headersNameTypes) {
      // Add existing columns (non-missing)
      headersNameTypes.forEach((header, index) => {
        if (!header.missing) {
          displayHeaders.push(headers[index]);
          displayTypes.push(types[index]);
        }
      });

      // Add selected missing columns
      selectedMissingColumns.forEach(missingCol => {
        displayHeaders.push(missingCol.title);
        displayTypes.push(missingCol.type);
      });
    } else {
      // Fallback to original behavior
      displayHeaders = headers;
      displayTypes = types;
    }

    table.createHeader(
      ...displayHeaders.map((header, index) => {
        const isNewColumn = selectedMissingColumns.some(
          col => col.title === header,
        );
        const headerText = `${header} (${displayTypes[index]})`;
        return isNewColumn
          ? i18next.t('pages.projects.projectDataActions.newColumnHeader', {
              header: headerText,
            })
          : headerText;
      }),
    );

    projectNames.forEach((projectName, rowIndex) => {
      // create each cell
      const cells = displayHeaders.map((header, colIndex) => {
        let cellValue = '';
        let displayValue = '';

        if (header.toLowerCase() === 'name') {
          cellValue = projectName;
          displayValue = cellValue;
        } else {
          // Check if this is an existing column
          const columnData = columnValues[rowIndex];
          if (columnData && columnData[header]) {
            cellValue = columnData[header].text || columnData[header].value;
            const columnType = columnData[header].type;
            displayValue = this.#formatCellValue(cellValue, columnType);
          } else {
            // This might be a missing column, get data from CSV
            const isSelectedMissingColumn = selectedMissingColumns.some(
              col => col.title === header,
            );
            if (isSelectedMissingColumn && csvData) {
              const { headers: csvHeaders, dataRows } = csvData;
              const csvColumnIndex = csvHeaders.indexOf(header);
              if (csvColumnIndex !== -1 && dataRows[rowIndex]) {
                const rawValue = dataRows[rowIndex][csvColumnIndex];
                const columnType = selectedMissingColumns.find(
                  col => col.title === header,
                ).type;
                displayValue = this.#formatCellValue(rawValue, columnType);
              }
            }
          }
        }

        // Handle empty values
        if (!displayValue || displayValue.trim() === '') {
          displayValue = '-';
        }

        // Truncate long values
        if (displayValue.length > 50) {
          displayValue = displayValue.substring(0, 47) + '...';
        }

        return displayValue;
      });

      // create the row
      table.addRow(...cells);
    });

    return table;
  }

  #formatCellValue(value, type) {
    if (!value) return '';

    switch (type) {
      case 'people':
        try {
          const userIds = JSON.parse(value);
          if (Array.isArray(userIds)) {
            return i18next.t(
              'pages.projects.projectDataActions.usersAssigned',
              {
                count: userIds.length,
                plural: userIds.length !== 1 ? 's' : '',
              },
            );
          }
        } catch (e) {
          return i18next.t('pages.projects.projectDataActions.invalidUserData');
        }
        break;

      case 'checkbox':
        return value === 'true' || value === true
          ? `✓ ${i18next.t('common.checked')}`
          : `✗ ${i18next.t('common.unchecked')}`;

      case 'date':
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString();
          }
        } catch (e) {
          return value;
        }
        break;

      case 'number':
        const num = parseFloat(value);
        if (!isNaN(num)) {
          return num.toLocaleString();
        }
        break;

      default:
        return value;
    }

    return value;
  }
}
